import { HubConnectionBuilder } from '@microsoft/signalr';
import { Observable } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';

import { BackendLoader } from '../api-env';
import { ApiOptions, type ApiOptionsInterface, type PartialApiOptionsInterface, WsOptions, WsOptionsInterface } from '../api-options';
import { UnsetApiOptions, UnsetWsOptions } from '../api-options.decorator';
import { DefaultParams } from '../default-params.model';
import { ParamsAdapter } from '../params.adapter';

/**
 * @internal
 */
export abstract class ApiBase {
  options: ApiOptions;

  constructor(
    protected http: HttpClient,
    protected defaultOptions: ApiOptionsInterface,
  ) {
    this.setOptions();
  }

  protected setOptions(options?: PartialApiOptionsInterface) {
    this.options = new ApiOptions({ ...this.defaultOptions, ...this.options, ...options });
  }

  protected unsetOptions() {
    this.options = new ApiOptions(this.defaultOptions);
  }

  @UnsetApiOptions()
  protected get<T>(path: string, params: ParamsAdapter = new DefaultParams()): Observable<T> {
    // If GET contains too much params, it will be refused by browser or server limit,
    // so on that case we switch to a POST
    if (this.isGetVerbOverLimit(path, params)) {
      this.options.simulateGetVerb = true;
      this.options.requestType = 'json';
      return this.post<T>(path, params);
    }
    return this.http.get<T>(path, {
      context: this.options.toHttpContext(),
      params: this.buildHttpParams(params),
      observe: this.options.responseObserve as 'body',
      responseType: this.options.responseType as 'json',
    });
  }

  @UnsetApiOptions()
  protected post<T>(path: string, params: ParamsAdapter = new DefaultParams()): Observable<T> {
    return this.http.post<T>(path, this.buildBodyRequest(params), {
      responseType: this.options.responseType as 'json',
      context: this.options.toHttpContext(),
      observe: this.options.responseObserve as 'body',
    });
  }

  @UnsetApiOptions()
  protected patch<T>(path: string, params: ParamsAdapter = new DefaultParams()): Observable<T> {
    return this.http.patch<T>(path, this.buildBodyRequest(params), { context: this.options.toHttpContext() });
  }

  @UnsetApiOptions()
  protected put<T>(path: string, params: ParamsAdapter = new DefaultParams()): Observable<T> {
    return this.http.put<T>(path, this.buildBodyRequest(params), { context: this.options.toHttpContext() });
  }

  @UnsetApiOptions()
  protected delete<T>(path: string, params: ParamsAdapter = new DefaultParams(), isUseParams: boolean = true): Observable<T> {
    return this.http.delete<T>(path, {
      params: isUseParams ? this.buildHttpParams(params) : undefined,
      body: !isUseParams ? this.buildBodyRequest(params) : undefined,
      context: this.options.toHttpContext(),
    });
  }

  private isGetVerbOverLimit(path: string, params: ParamsAdapter) {
    const payload = params.toObject();
    const httpParams = new HttpParams({ fromObject: payload as Record<string, string | string[]> });
    // Server limit is 4ko
    // we could even reduce this, because of browser limit
    return (path + '?' + httpParams.toString()).length >= this.options.maxSizeGetParams;
  }

  private buildBodyRequest(params: ParamsAdapter) {
    switch (this.options.requestType) {
      case 'form':
        return params.toFormData(this.options.backendId === 'midBack');
      case 'json':
        return params.toObject(this.options.backendId === 'midBack');
      case 'raw':
        return params.getParams();
    }
  }

  private buildHttpParams(params: ParamsAdapter) {
    return params.toObject(this.options.backendId === 'midBack') as HttpParams;
  }
}

/**
 * @internal
 */
export abstract class WsBase {
  options: WsOptions;

  constructor(
    protected loader: BackendLoader,
    protected accessToken: string,
    protected defaultOptions: WsOptionsInterface,
  ) {
    this.setOptions();
  }

  protected setOptions(options?: WsOptionsInterface) {
    this.options = new WsOptions({ ...this.defaultOptions, ...this.options, ...options });
  }

  protected unsetOptions() {
    this.options = new WsOptions(this.defaultOptions);
  }

  @UnsetWsOptions()
  protected build(path: string) {
    return new HubConnectionBuilder()
      .withUrl(`${this.options.getBaseUrl(this.loader)}${path}`, this.options.getHttpConnectionOptions(this.accessToken))
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 10 * 60 * 1000) {
            // If we've been reconnecting for less than 10 minutes so far, retry after 60 seconds.
            return 60 * 1000;
          } else {
            // If we've been reconnecting for more than 10 minutes so far, stop reconnecting.
            return null;
          }
        },
      })
      .build();
  }
}
